import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { DenaliScopeProvider, DEFAULT_LABELS } from '../DenaliAnalysis/filters/DenaliScopeProvider'
import Loading from '../../molecules/Loading'
import useBlueprintPresentation from './hooks/useBlueprintPresentation'

/**
 * Self-configuring scope provider that derives its scopes from a blueprint's
 * Scope Selector presentation config and the referenced purpose's scopePredicates.
 *
 * Wraps children in a DenaliScopeProvider so that both QBP presentations
 * (via useBlueprintScopeResolution) and DA components (when combined with
 * QBPScopeFilterBridge) can consume the scope state.
 */
function QBPScopeProvider ({ blueprintCode, presentationKey, storageKey, children }) {
  const {
    presentationConfig,
    definition,
    isLoading,
    error
  } = useBlueprintPresentation(blueprintCode, presentationKey)

  const config = presentationConfig?.config

  const scopes = useMemo(() => {
    if (!config || !definition) return []

    const purposes = definition.data?.purposes ?? {}

    if (config.mode === 'purpose') {
      return (config.purposes ?? [])
        .filter(p => p.purposeCode)
        .map(p => ({
          name: p.purposeCode,
          path: [],
          labels: {
            ...DEFAULT_LABELS,
            allItems: p.displayName || p.purposeCode
          },
          icon: p.icon
        }))
    }

    if (config.mode === 'value-source') {
      return (config.scopes ?? [])
        .filter(cs => cs.scopeType && cs.sourcePurposeCode && cs.valueColumn)
        .map(cs => ({
          name: cs.scopeType,
          path: [],
          labels: {
            ...DEFAULT_LABELS,
            ...cs.labels,
            allItems: cs.labels?.allItems ?? `All ${cs.scopeType}s`
          },
          icon: cs.icon,
          source: {
            type: 'valueSource',
            purposeCode: cs.sourcePurposeCode,
            valueColumn: cs.valueColumn,
            labelColumn: cs.labelColumn ?? cs.valueColumn
          }
        }))
    }

    if (config.mode === 'mixed') {
      return (config.scopes ?? [])
        .map(item => {
          const sourceType = item?.source?.type
          if (sourceType === 'membership') {
            const sourcePurpose = purposes[item.source.purposeCode]
            const pred = (sourcePurpose?.scopePredicates ?? [])
              .find(p => p.type === 'membership' && p.scopeType === item.scopeType)
            if (!pred) return null
            return {
              name: item.scopeType,
              path: pred.path,
              labels: {
                ...DEFAULT_LABELS,
                ...item.labels,
                allItems: item.labels?.allItems ?? `All ${item.scopeType}s`
              },
              icon: item.icon,
              source: { type: 'membership' }
            }
          }
          if (sourceType === 'valueSource') {
            return {
              name: item.scopeType,
              path: [],
              labels: {
                ...DEFAULT_LABELS,
                ...item.labels,
                allItems: item.labels?.allItems ?? `All ${item.scopeType}s`
              },
              icon: item.icon,
              source: {
                type: 'valueSource',
                purposeCode: item.source.purposeCode,
                valueColumn: item.source.valueColumn,
                labelColumn: item.source.labelColumn ?? item.source.valueColumn
              }
            }
          }
          return null
        })
        .filter(Boolean)
    }

    // Membership mode (default)
    const purposeData = purposes[config.purposeCode]
    const predicates = (purposeData?.scopePredicates ?? [])
      .filter(p => p.type === 'membership')

    const configScopes = config.scopes ?? []

    if (configScopes.length) {
      return configScopes
        .map(cs => {
          const pred = predicates.find(p => p.scopeType === cs.scopeType)
          if (!pred) return null
          return {
            name: cs.scopeType,
            path: pred.path,
            labels: { ...DEFAULT_LABELS, ...cs.labels },
            icon: cs.icon,
            source: { type: 'membership' }
          }
        })
        .filter(Boolean)
    }

    return predicates.map(pred => ({
      name: pred.scopeType,
      path: pred.path,
      labels: {
        ...DEFAULT_LABELS,
        allItems: `All ${pred.scopeType}s`
      },
      icon: null,
      source: { type: 'membership' }
    }))
  }, [config, definition])

  if (isLoading) {
    return <Loading />
  }

  if (error) {
    return (
      <div className='p-md font-size-sm' style={{ color: 'var(--color-danger-500)' }}>
        Failed to load scope config from &quot;{blueprintCode}&quot;: {error.message ?? 'Unknown error'}
      </div>
    )
  }

  return (
    <DenaliScopeProvider storageKey={storageKey ?? `qbp_${blueprintCode}_${presentationKey}`} scopes={scopes}>
      {children}
    </DenaliScopeProvider>
  )
}

QBPScopeProvider.propTypes = {
  /** Blueprint code to load the scope selector config from */
  blueprintCode: PropTypes.string.isRequired,

  /** Presentation key within the blueprint's presentation.options that holds the Scope Selector config */
  presentationKey: PropTypes.string.isRequired,

  /** LocalStorage key for persisting scope selection. Defaults to `qbp_{blueprintCode}_{presentationKey}` */
  storageKey: PropTypes.string,

  children: PropTypes.node
}

export default QBPScopeProvider
